'use client';
/* eslint-disable react-hooks/rules-of-hooks */
import { useContext, createContext, useRef, useEffect, useCallback, ReactNode } from 'react';
import { useRouter, NextRouter } from 'next/router';
import { useRouter as useAppRouter, usePathname } from 'next/navigation';
import { useSyncStudyStore } from '@mildang/design-system-legacy/esm/hooks/syncStudyStore';
import { useRouterSystem } from '@mildang/design-system-legacy/esm/context/routerSystem';
import useShowWarning from '@mildang/design-system-legacy/esm/hooks/useShowWarning';

type PushParams = Parameters<NextRouter['push']>;

type CustomRouter = NextRouter & {
  parentPush: (...args: PushParams) => Promise<boolean>;
  backToParent: () => void;
  back: () => void;
  pushParentUrl: () => void;
  setParentUrlFromPrevUrl: () => void;
  forcePush: (...args: PushParams) => Promise<boolean>;
};

const SESSION_STORE_KEY = '__mil_next_parent_';
const INDEX_SESSION_STORE_KEY = '__mil_next_parent_index';

type HistoryStack = Array<string>;

function getHistorySessionIndex() {
  if (typeof window === 'undefined') {
    return 0;
  }
  return Number(sessionStorage.getItem(`${INDEX_SESSION_STORE_KEY}`) || 0);
}

type Context = {
  idx: number;
  isLoading: boolean;
};
type ContextWithRef = {
  current: Context;
};

const defaultContext = { current: { idx: 0, isLoading: false } };
const CustomHistoryContext = createContext<ContextWithRef>(defaultContext);

const isSSR = typeof window === 'undefined';

export const ContextProvider = ({ children }: { children: ReactNode }) => {
  const router = useRouter();

  const stateRef = useRef<Context>({
    idx: getHistorySessionIndex(),
    isLoading: false,
  });

  const historyState = useRef<{ idx: number }>(isSSR ? undefined : globalThis?.history?.state);

  useEffect(() => {
    const handleStart = (url: string) => {
      stateRef.current.isLoading = true;
    };
    const handleComplete = (): void => {
      stateRef.current.isLoading = false;
      if (historyState.current.idx > history.state.idx) {
        stateRef.current.idx--;
      } else if (historyState.current.idx < history.state.idx) {
        stateRef.current.idx++;
      }
      sessionStorage.setItem(`${INDEX_SESSION_STORE_KEY}`, String(stateRef.current.idx));
      historyState.current = history.state;
    };
    const handleError = () => {
      stateRef.current.isLoading = false;
    };

    router.events.on('routeChangeComplete', handleComplete);
    router.events.on('routeChangeStart', handleStart);
    router.events.on('routeChangeError', handleError);

    return () => {
      router.events.off('routeChangeStart', handleStart);
      router.events.off('routeChangeComplete', handleComplete);
      router.events.on('routeChangeError', handleError);
    };
  }, [router]);

  return <CustomHistoryContext.Provider value={stateRef}>{children}</CustomHistoryContext.Provider>;
};

const getParentHistoryStackFromIdx = (idx: number): HistoryStack => {
  try {
    const historyStackStr = sessionStorage.getItem(`${SESSION_STORE_KEY}${idx}`);
    if (!historyStackStr) {
      return [];
    }
    return JSON.parse(historyStackStr!) as HistoryStack;
  } catch (error) {
    return [];
  }
};

const setParentHistoryStack = (idx: number, nextStack: HistoryStack) => {
  sessionStorage.setItem(`${SESSION_STORE_KEY}${idx}`, JSON.stringify(nextStack));
};

// https://github.com/vercel/next.js/blob/2518a5a4c71cd06ad87548ef90bb7ae884a28c67/packages/next/shared/lib/router/router.ts#L598
function useCustomRouter(): CustomRouter {
  const routeType = useRouterSystem();
  if (routeType === 'app') {
    const pathname = usePathname();
    const router = useAppRouter();
    const { sync } = useSyncStudyStore();
    const showWarning = useShowWarning();
    const showWarningRef = useRef(showWarning);
    useEffect(() => {
      showWarningRef.current = showWarning;
    }, [showWarning]);

    const confirmNavigation = () => {
      if (!showWarningRef.current) return true;
      return window.confirm('지금 나가면 입력한 내용이 저장되지 않습니다. 페이지를 나갈까요?');
    };

    return {
      ...router,
      pathname,
      push: async (url: string, as: string, options: any) => {
        if (!confirmNavigation()) return false;
        router.push(url.toString(), options);
        return true;
      },
      forcePush: async (url: string, as: string, options: any) => {
        router.push(url.toString(), options);
        return true;
      },
      parentPush: async (url: string, as: string, options: any) => {
        if (!confirmNavigation()) return false;
        router.push(url.toString(), options);
        return true;
      },
      backToParent: () => {
        console.log('backToParent deprecated');
      },
      pushParentUrl: () => {
        console.log('pushParentUrl deprecated');
      },
      setParentUrlFromPrevUrl: () => {
        console.log('setParentUrlFromPrevUrl deprecated');
      },
      replace: async (url: string, as: string, options: any) => {
        if (sync) return false;
        router.replace(url.toString(), options);
        return true;
      },
    } as any;
  } else {
    const router = useRouter();

    const { sync } = useSyncStudyStore();
    const historyContext = useContext(CustomHistoryContext);

    const setParentUrlFromPrevUrl = () => {
      const prevIdexParentHistory = getParentHistoryStackFromIdx(historyContext.current.idx - 1);
      if (prevIdexParentHistory) {
        setParentHistoryStack(historyContext.current.idx, prevIdexParentHistory);
      }
    };

    const pushParentUrl = useCallback(() => {
      const stack = getParentHistoryStackFromIdx(historyContext.current.idx - 1);
      setParentHistoryStack(historyContext.current.idx, [...stack, router.asPath]);
    }, [router, historyContext]);

    const parentPush = useCallback(
      async (...args: PushParams) => {
        try {
          pushParentUrl();
        } catch {}
        return await router.push(...args);
      },
      [router, pushParentUrl],
    );

    const backToParent = (fallbackUrl = '/') => {
      if (historyContext.current.isLoading) {
        return;
      }
      const stack = getParentHistoryStackFromIdx(historyContext.current.idx - 1);
      const lastParentUrl = stack.pop();

      setParentHistoryStack(historyContext.current.idx, stack);

      if (lastParentUrl) {
        router.push(lastParentUrl);
      } else {
        router.push(fallbackUrl);
      }
    };

    const push = async (...args: PushParams): Promise<boolean> => {
      if (sync) return false;
      setParentUrlFromPrevUrl();
      return await router.push(...args);
    };

    const forcePush = async (...args: PushParams): Promise<boolean> => {
      setParentUrlFromPrevUrl();
      return await router.push(...args);
    };

    const replace = async (...args: PushParams): Promise<boolean> => {
      if (sync) return false;
      return await router.replace(...args);
    };

    return {
      ...router,
      push,
      forcePush,
      parentPush,
      backToParent,
      pushParentUrl,
      setParentUrlFromPrevUrl,
      replace,
    };
  }
}

export default useCustomRouter;
